import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { adminApi } from '@/services/api/admin'
import type { SubstitutionStatsResponse } from '@/services/api/admin'
import type { RadarBubbleLesson } from '@/services/api/teacher'
import type { RadarBubble, RadarZones, RadarRay, RadarConfigResponse } from '@/services/api/teacher'
import { AdminRadar } from '@/components/radar/AdminRadar'
import { AdminSlideOver } from '@/components/radar/AdminSlideOver'
import { MessageModal } from '@/components/radar/MessageModal'
import { useRadarUserPrefs } from '@/components/radar/hooks/useRadarUserPrefs'
import { RequestListTooltip } from '@/components/admin/RequestListTooltip'
import { AdminRequestCardModal } from '@/components/admin/AdminRequestCardModal'
import { useToast } from '@/components/ui/ToastProvider'
import { extractErrorMessage } from '@/utils/errorHandler'
import { getSavedTimeRangeDays, saveTimeRangeDays, DEFAULT_TIME_RANGE_DAYS } from '@/utils/radarStorage'
import { useAdminDashboardConfig } from '@/hooks/useAdminDashboardConfig'
import { onCascadeStateChanged } from '@/utils/cascadeEvents'

import { FinanceDecisionDialog } from '@/components/teacher/FinanceDecisionDialog'
import { financeUiEnabled } from '@/utils/financeUi'
import { useDirectorEnabled } from '@/hooks/useDirectorEnabled'
import { TestRollbackButton } from '@/components/email/TestRollbackButton'

interface SubstitutionListRow {
  id: string
  status: string
  teacher_name?: string | null
  requesting_teacher?: string | null
  date?: string | null
  date_to?: string | null
  lesson_count?: number
  can_change_requester_finance?: boolean
  hr_module_enabled?: boolean
  lesson_details?: RadarBubbleLesson[] | null
  hours_until_lesson?: number | null
  effective_lesson_count?: number | null
  original_lesson_count?: number | null
  requester_deduction?: string | null
  requester_school_default?: string | null
}

export function SubstitutionManagementPage() {
  const navigate = useNavigate()
  const directorEnabled = useDirectorEnabled()
  const [searchParams, setSearchParams] = useSearchParams()
  const [stats, setStats] = useState<SubstitutionStatsResponse | null>(null)
  const [requests, setRequests] = useState<SubstitutionListRow[]>([])
  const [financeRequestId, setFinanceRequestId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null)
  const [slideOverRequestId, setSlideOverRequestId] = useState<string | null>(null)
  const [slideOverBubble, setSlideOverBubble] = useState<RadarBubble | null>(null)
  const [cardModalRequestId, setCardModalRequestId] = useState<string | null>(null)
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const { showToast } = useToast()
  const { data: dashboardConfig } = useAdminDashboardConfig()

  // Radar state
  const [radarBubbles, setRadarBubbles] = useState<RadarBubble[]>([])
  const [radarZones, setRadarZones] = useState<RadarZones | undefined>()
  const [radarRays, setRadarRays] = useState<RadarRay[]>([])
  const [radarTimeRange, setRadarTimeRange] = useState<{ days: number; from: string; to: string } | undefined>()
  const [radarConfig, setRadarConfig] = useState<RadarConfigResponse | null>(null)
  const [radarLoading, setRadarLoading] = useState(false)
  const [radarConfigLoading, setRadarConfigLoading] = useState(false)
  // Cache saved value in ref to avoid double read
  const savedDaysRef = useRef<number | null>(null)
  if (savedDaysRef.current === null) {
    savedDaysRef.current = getSavedTimeRangeDays('admin')
  }
  // Load saved time range from localStorage, fallback to 60 days
  const [timeRangeDays, setTimeRangeDays] = useState(() => {
    return savedDaysRef.current ?? DEFAULT_TIME_RANGE_DAYS
  })

  // Ref to keep timeRangeDays stable for fetchRadar
  const timeRangeDaysRef = useRef(timeRangeDays)
  timeRangeDaysRef.current = timeRangeDays

  // Per-user radar prefs (filter + server-side time-range persistence) — filter-fix-v2
  const radarPrefs = useRadarUserPrefs('admin', adminApi, radarConfig?.default_time_range_days)

  // Request sequencing: only the latest radar fetch may commit state.
  const reqGenRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)
  // Guard the mount fetches so React.StrictMode's dev double-invoke does not fire
  // duplicate network requests (which would otherwise cause a double config load
  // -> double radar fetch where the first is aborted).
  const initRef = useRef(false)
  const serverRangeAppliedRef = useRef(false)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    try {
      const res = await adminApi.getSubstitutionStats()
      setStats(res)
      setError(null)
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Statistiken konnten nicht geladen werden',
      })
      console.error('Failed to load substitution stats:', err)
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  const subLimit = dashboardConfig?.admin_list_limit_substitutions ?? 100

  const fetchRequests = useCallback(async () => {
    try {
      const res = await adminApi.listSubstitutions({ skip: 0, limit: subLimit })
      setRequests(res.substitutions || [])
    } catch {
      // Non-fatal
    }
  }, [subLimit])

  const fetchRadarConfig = useCallback(async () => {
    setRadarConfigLoading(true)
    try {
      const config = await adminApi.getRadarConfig()
      setRadarConfig(config)
      // Use cached saved value if available, otherwise use config default
      // Clamp to config limits to ensure value is valid
      if (!serverRangeAppliedRef.current) {
        const initialDays = savedDaysRef.current ?? config.default_time_range_days
        const clampedDays = Math.max(
          config.min_time_range_days,
          Math.min(config.max_time_range_days, initialDays)
        )
        setTimeRangeDays(clampedDays)
      }
    } catch {
      // Non-fatal
    } finally {
      setRadarConfigLoading(false)
    }
  }, [])

  const fetchRadar = useCallback(async (days?: number) => {
    // Cancel any in-flight request and start a new generation (latest-wins).
    abortRef.current?.abort()
    const gen = ++reqGenRef.current
    const controller = new AbortController()
    abortRef.current = controller
    const effectiveDays = days ?? timeRangeDaysRef.current

    if (gen === reqGenRef.current && isMountedRef.current) {
      setRadarLoading(true)
    }
    try {
      const radarData = await adminApi.getAdminRadar(effectiveDays, controller.signal)
      // Atomic commit: only the latest generation may update bubble state.
      if (gen !== reqGenRef.current || !isMountedRef.current) return
      setRadarBubbles(radarData.bubbles)
      setRadarZones(radarData.zones)
      setRadarRays(radarData.rays)
      setRadarTimeRange(radarData.time_range)
    } catch (err) {
      // Aborted requests (superseded by a newer slider value) are expected.
      const e = err as { name?: string; code?: string }
      const aborted =
        e?.name === 'CanceledError' ||
        e?.name === 'AbortError' ||
        e?.code === 'ERR_CANCELED'
      if (aborted || gen !== reqGenRef.current) return
      console.error('Failed to load radar:', err)
    } finally {
      if (gen === reqGenRef.current && isMountedRef.current) {
        setRadarLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (initRef.current) return
    initRef.current = true
    fetchStats()
    fetchRequests()
    fetchRadarConfig()
  }, [fetchStats, fetchRequests, fetchRadarConfig])

  useEffect(() => {
    if (radarConfig) {
      fetchRadar()
    }
  }, [radarConfig, fetchRadar])

  useEffect(() => {
    if (!radarPrefs.isLoaded) return
    const days = radarPrefs.timeRangeDays
    if (days == null) {
      serverRangeAppliedRef.current = true
      return
    }
    const min = radarConfig?.min_time_range_days ?? 1
    const max = radarConfig?.max_time_range_days ?? 365
    const clamped = Math.max(min, Math.min(max, days))
    serverRangeAppliedRef.current = true
    if (clamped === timeRangeDaysRef.current) return
    setTimeRangeDays(clamped)
    saveTimeRangeDays(clamped, 'admin')
    fetchRadar(clamped)
  }, [radarPrefs.isLoaded, radarPrefs.timeRangeDays, radarConfig, fetchRadar])

  useEffect(() => {
    if (!radarConfig) return
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      fetchRadar()
      fetchStats()
      fetchRequests()
    }, 60000)
    return () => clearInterval(id)
  }, [radarConfig, fetchRadar, fetchStats, fetchRequests])

  useEffect(() => {
    return onCascadeStateChanged(() => {
      if (document.visibilityState === 'visible') {
        fetchRadar()
        fetchStats()
        fetchRequests()
      }
    })
  }, [fetchRadar, fetchStats, fetchRequests])

  // Cancel any in-flight radar request and pending debounce on unmount.
  // Reset isMounted in setup so React.StrictMode's dev double-mount (setup ->
  // cleanup -> setup) does not leave the flag stuck on false.
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      abortRef.current?.abort()
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  useEffect(() => {
    const detailParam = searchParams.get('detail')
    const bubbleParam = searchParams.get('bubble')
    if (detailParam) {
      setSlideOverRequestId(detailParam)
      if (bubbleParam && radarBubbles.length > 0) {
        const found = radarBubbles.find(b => b.id === bubbleParam)
        setSlideOverBubble(found || null)
      }
    } else {
      setSlideOverRequestId(null)
      setSlideOverBubble(null)
    }
  }, [searchParams, radarBubbles])

  const handleDelete = async (requestId: string) => {
    try {
      await adminApi.deleteSubstitution(requestId)
      setSuccess('Vertretung gelöscht')
      setShowConfirmDelete(null)
      cancelDebounce()
      fetchStats()
      fetchRequests()
      fetchRadar()
      setTimeout(() => setSuccess(null), 3000)
    } catch {
      setError('Konnte Vertretung nicht löschen')
    }
  }

  const handleBubbleClick = (bubble: RadarBubble) => {
    setSlideOverRequestId(bubble.request_id)
    setSlideOverBubble(bubble)
    setSearchParams({ detail: bubble.request_id, bubble: bubble.id }, { replace: true })
  }

  const handleCloseSlideOver = () => {
    setSlideOverRequestId(null)
    setSlideOverBubble(null)
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('detail')
    newParams.delete('bubble')
    setSearchParams(newParams, { replace: true })
  }

  const cancelDebounce = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
  }

  const handleTimeRangeChange = (days: number) => {
    if (radarPrefs.isLocked) return // locked teacher: keep the standard view
    // UI (slider thumb) and persistence react immediately; the network fetch is
    // debounced so rapid dragging does not fire one request per tick.
    setTimeRangeDays(days)
    saveTimeRangeDays(days, 'admin')
    radarPrefs.setTimeRange(days) // server-side persistence (filter-fix-v2)
    cancelDebounce()
    debounceRef.current = setTimeout(() => {
      fetchRadar(days)
    }, 300)
  }

  const handleRefreshRadar = () => {
    cancelDebounce()
    fetchRadar()
    fetchStats()
    fetchRequests()
  }

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-green-100 text-green-800',
    declined: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
    booked: 'bg-blue-100 text-blue-800',
    partially_filled: 'bg-orange-100 text-orange-800',
    director_pending: 'bg-indigo-100 text-indigo-800',
    director_returned: 'bg-indigo-100 text-indigo-800',
  }

  const statusLabels: Record<string, string> = {
    pending: 'Offen', accepted: 'Akzeptiert', declined: 'Abgelehnt',
    cancelled: 'Storniert', booked: 'Gebucht', partially_filled: 'Teilweise',
    director_pending: 'Direktor-Freigabe ausstehend',
    director_returned: 'Zurückgestellt (angepasst)',
  }
  const kpiKeys = (Object.keys(statusLabels) as Array<keyof typeof statusLabels>).filter(
    key => directorEnabled || (key !== 'director_pending' && key !== 'director_returned'),
  )

  const statusDotColor = (status: string): string =>
    status === 'accepted' || status === 'booked' ? '#22c55e' :
    status === 'pending' ? '#f97316' :
    status === 'director_pending' || status === 'director_returned' ? '#818cf8' :
    status === 'declined' ? '#ef4444' : '#94a3b8'

  if (loading) return <div className="p-6 text-gray-500">Laden...</div>

  return (
    <div className="p-6">
      <TestRollbackButton />
      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      {success && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{success}</div>}

      {/* Status counts */}
      <div className={`grid grid-cols-2 md:grid-cols-4 ${directorEnabled ? 'lg:grid-cols-9' : 'lg:grid-cols-7'} gap-3 mb-6`}>
        {kpiKeys.map((key) => (
          <div key={key} className={`p-3 rounded-lg text-center ${statusColors[key]}`}>
            <div className="text-xl font-bold">{stats ? (stats[key as keyof SubstitutionStatsResponse] ?? 0) : 0}</div>
            <div className="text-xs font-medium">{statusLabels[key]}</div>
          </div>
        ))}
        <div className="p-3 rounded-lg text-center bg-purple-100 text-purple-800">
          <div className="text-xl font-bold">{stats?.total || 0}</div>
          <div className="text-xs font-medium">Total</div>
        </div>
      </div>

      {/* Radar */}
      <div className="mb-6">
        <AdminRadar
          directorEnabled={directorEnabled}
          bubbles={radarBubbles}
          zones={radarZones}
          rays={radarRays}
          timeRange={radarTimeRange}
          config={radarConfig}
          isLoading={radarLoading}
          isConfigLoading={radarConfigLoading}
          onBubbleClick={handleBubbleClick}
          onTimeRangeChange={handleTimeRangeChange}
          onRefresh={handleRefreshRadar}
          filterState={radarPrefs.filterState}
          onFilterChange={radarPrefs.setFilter}
          onResetFilter={radarPrefs.resetFilter}
          isFilterLocked={radarPrefs.isLocked}
          filterDefaultState={radarPrefs.defaultState}
        />
      </div>

      {/* Recent requests list */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border-l-4 border-l-gray-400">
        <div className="p-3 border-b border-gray-200">
          <h3 className="font-semibold text-sm">Aktuelle Anfragen</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {requests.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">Keine Anfragen</div>
          ) : (
            requests.slice(0, 20).map((r: SubstitutionListRow) => (
              <div
                key={r.id}
                onClick={() => setCardModalRequestId(r.id)}
                onMouseEnter={() => setHoveredRowId(r.id)}
                onMouseLeave={() => setHoveredRowId(null)}
                className="p-3 flex items-center justify-between hover:bg-gray-50 relative cursor-pointer"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="w-2.5 h-2.5 rounded-full" style={{
                      backgroundColor: statusDotColor(r.status)
                    }} />
                    <span className="font-medium text-sm">{r.teacher_name || r.requesting_teacher || '—'}</span>
                    <span className="text-xs text-gray-500">
                      {r.date ? new Date(r.date).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
                      {r.date_to && ` – ${new Date(r.date_to).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                    </span>
                    <span className="text-xs text-gray-600 font-medium">
                      {r.lesson_count} {r.lesson_count === 1 ? 'Lektion' : 'Lektionen'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[r.status] || 'bg-gray-100 text-gray-600'}`}>
                    {(!directorEnabled && (r.status === 'director_pending' || r.status === 'director_returned'))
                      ? statusLabels.pending
                      : (statusLabels[r.status] || r.status)}
                  </span>
                  {(r.status === 'booked' || r.status === 'accepted' || r.status === 'pending') && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowConfirmDelete(r.id) }}
                      className="text-xs text-red-600 hover:text-red-800 px-2 py-1 border border-red-200 rounded hover:bg-red-50"
                    >
                      Löschen
                    </button>
                  )}
                  {financeUiEnabled(r.hr_module_enabled) && r.can_change_requester_finance === true && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setFinanceRequestId(r.id) }}
                      className="text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 border border-indigo-200 rounded hover:bg-indigo-50"
                      title="Finanz-Entscheidung (a): Stundenkonto (IPB) oder Lohn"
                    >
                      Finanz
                    </button>
                  )}
                </div>
                
                {/* Hover Tooltip */}
                {r.lesson_details && r.lesson_details.length > 0 && (
                  <RequestListTooltip
                    lessonDetails={r.lesson_details}
                    visible={hoveredRowId === r.id}
                    statusLabel={statusLabels[r.status] || r.status}
                    statusColor={statusDotColor(r.status)}
                    date={r.date ?? undefined}
                    hoursUntilLesson={r.hours_until_lesson ?? undefined}
                    effectiveLessonCount={r.effective_lesson_count ?? undefined}
                    originalLessonCount={r.original_lesson_count ?? undefined}
                  />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Confirm delete modal */}
      {showConfirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm">
            <h3 className="font-bold text-lg mb-2">Vertretung löschen?</h3>
            <p className="text-gray-600 mb-4">Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmDelete(null)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={() => handleDelete(showConfirmDelete)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Slide-over Panel */}
      {slideOverRequestId && (
        <AdminSlideOver
          requestId={slideOverRequestId}
          bubble={slideOverBubble || radarBubbles.find(b => b.request_id === slideOverRequestId)}
          allBubbles={radarBubbles}
          onClose={handleCloseSlideOver}
          onRefresh={() => {
            fetchRadar()
            fetchStats()
            fetchRequests()
          }}
          onOpenMessageModal={() => setShowMessageModal(true)}
          switchView={{
            label: 'Live-View',
            onClick: () => navigate(`/admin/live-status?detail=${slideOverRequestId}`),
          }}
        />
      )}

      {/* Card Modal (Lehrer-Sicht der Anfrage) */}
      {cardModalRequestId && (
        <AdminRequestCardModal
          requestId={cardModalRequestId}
          onClose={() => setCardModalRequestId(null)}
          onChanged={() => {
            fetchRequests()
            fetchStats()
            fetchRadar()
          }}
        />
      )}

      {/* Message Modal */}
      {showMessageModal && slideOverRequestId && (() => {
        const bubble = radarBubbles.find(b => b.request_id === slideOverRequestId)
        if (!bubble) return null
        return (
          <MessageModal
            bubble={bubble}
            onClose={() => setShowMessageModal(false)}
            onSuccess={() => {
              setShowMessageModal(false)
              fetchRadar()
              showToast({ message: 'Nachricht gesendet', type: 'success', duration: 3000 })
            }}
          />
        )
      })()}
      <FinanceDecisionDialog
        isOpen={financeRequestId !== null}
        kind="requester_deduction"
        requestId={financeRequestId ?? ''}
        currentValue={requests.find(r => r.id === financeRequestId)?.requester_deduction ?? null}
        personalValue={requests.find(r => r.id === financeRequestId)?.requester_deduction ?? null}
        schoolDefault={
          requests.find(r => r.id === financeRequestId)?.requester_school_default ?? 'ipb'
        }
        onClose={() => setFinanceRequestId(null)}
        onSuccess={() => {
          setFinanceRequestId(null)
          fetchRequests()
        }}
      />
    </div>
  )
}